import React, { ReactNode, RefObject } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// Custom CSS to remove hover effects on country selection
const phoneInputStyles = `
  .react-tel-input .country-list .country:hover {
    background-color: transparent !important;
  }
  .react-tel-input .country-list .country.highlight {
    background-color: transparent !important;
  }
`;

/**
 * SignupPhoneNumberInput Component
 *
 * A phone number input component specifically designed for the signup screen.
 * Uses react-phone-input-2 with international country selection.
 * Returns clean phone numbers (digits only) and country data to the parent.
 *
 * @example
 * <SignupPhoneNumberInput
 *   id="phoneNumber"
 *   label="Phone Number"
 *   value={values.phoneNumber}
 *   onChange={(cleanNumber, data) => {
 *     setFieldValue('phoneNumber', cleanNumber);
 *     setFieldValue('dial_code', data?.dialCode || null);
 *     setFieldValue('iso_code', data?.countryCode || null);
 *   }}
 *   isInvalid={!!errors.phoneNumber}
 *   errorMessage={errors.phoneNumber}
 * />
 */

interface SignupPhoneNumberInputProps {
  id?: string;
  name?: string;
  children?: ReactNode;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  description?: ReactNode;
  errorMessage?: string | undefined;
  validate?: any;
  validationBehavior?: 'native' | 'aria';
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  labelPlacement?: 'outside' | 'inside';
  fullWidth?: boolean;
  isClearable?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  baseRef?: RefObject<HTMLDivElement>;
  validationState?: 'valid' | 'invalid';
  disableAnimation?: boolean;
  className?: string;
  classNames?: Record<string, string>;
  customClassNames?: {
    label?: string;
    input?: string[];
    innerWrapper?: string;
    inputWrapper?: string[];
  };
  onChange?: (value: string, data?: any) => void;
  onValueChange?: (value: string) => void;
  onClear?: () => void;
  variant?: 'flat' | 'faded' | 'bordered' | 'underlined' | undefined;
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | undefined;
  size?: 'md' | 'sm' | 'lg' | undefined;
  radius?: 'md' | 'sm' | 'lg' | 'none' | 'full' | undefined;
  label?: string | undefined;
  style?: React.CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  // react-phone-input-2 specific props
  country?: string;
  onlyCountries?: string[];
  preferredCountries?: string[];
  excludeCountries?: string[];
  enableSearch?: boolean;
  autoFormat?: boolean;
}

const SignupPhoneNumberInput: React.FC<SignupPhoneNumberInputProps> = ({
  id,
  name,
  children,
  value,
  defaultValue,
  placeholder,
  description,
  errorMessage,
  validate,
  validationBehavior = 'aria',
  startContent,
  endContent,
  labelPlacement = 'outside',
  fullWidth = true,
  isClearable = false,
  isRequired = false,
  isReadOnly = false,
  isDisabled = false,
  isInvalid = false,
  baseRef,
  validationState,
  disableAnimation = false,
  className,
  classNames,
  customClassNames,
  onChange,
  onValueChange,
  onClear,
  variant = 'flat',
  color = 'default',
  size = 'md',
  radius = 'sm',
  label,
  style,
  onFocus,
  onBlur,
  disabled,
  // react-phone-input-2 specific props
  country = 'nz',
  onlyCountries,
  preferredCountries,
  excludeCountries,
  enableSearch = true,
  autoFormat = true,
}) => {
  const handleChange = (value: string, data?: any) => {
    if (onChange) onChange(value, data);
    if (onValueChange) onValueChange(value);
  };

  // Create class names that match TextInput styling
  const containerClass = `${classNames?.container || ''}`;
  const labelClass = `text-foreground-700 font-semibold text-small pb-1 will-change-auto origin-top-left transition-all !duration-200 !ease-out motion-reduce:transition-none
  } ${customClassNames?.label || ''} ${classNames?.label || ''}`;

  const phoneInputClass = `
      ${customClassNames?.input?.join(' ') || ''}
      ${isDisabled || disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${isReadOnly ? 'bg-gray-50' : ''}
    `.trim();

  // Style the phone input to match NextUI bordered variant
  const phoneInputStyle = {
    ...style,
    width: fullWidth ? '100%' : 'auto',
  };

  const inputStyle = {
    width: '100%',
    height: size === 'sm' ? '32px' : size === 'lg' ? '48px' : '40px',
    border: variant === 'bordered' ? '2px solid #e4e4e7' : 'none',
    borderRadius: radius === 'sm' ? '6px' : radius === 'lg' ? '14px' : '8px',
    padding: '8px 12px',
    paddingLeft: startContent ? '52px' : '52px', // Space for flag + startContent
    fontSize: size === 'sm' ? '14px' : size === 'lg' ? '16px' : '14px',
    backgroundColor: isReadOnly
      ? '#f4f4f5'
      : variant === 'flat'
        ? '#f4f4f5'
        : 'transparent',
    outline: 'none',
    transition: 'all 0.2s ease',
    opacity: isDisabled || disabled ? 0.5 : 1,
    cursor: isDisabled || disabled ? 'not-allowed' : 'default',
  };

  const buttonStyle = {
    border: 'none',
    backgroundColor: 'transparent',
    padding: '0 8px',
    borderRadius: `${radius === 'sm' ? '6px' : radius === 'lg' ? '14px' : '8px'} 0 0 ${radius === 'sm' ? '6px' : radius === 'lg' ? '14px' : '8px'}`,
  };

  return (
    <div
      className={`group flex flex-col data-[hidden=true]:hidden ${containerClass}`}
      ref={baseRef}
    >
      {/* Inject custom CSS to remove hover effects */}
      <style dangerouslySetInnerHTML={{ __html: phoneInputStyles }} />

      {label && labelPlacement === 'outside' && (
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
      )}

      <div className="relative w-full inline-flex tap-highlight-transparent flex-row items-center shadow-sm">
        {startContent && (
          <div className="absolute left-12 top-1/2 transform -translate-y-1/2 z-10">
            {startContent}
          </div>
        )}
        <PhoneInput
          country={country}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled || disabled}
          onlyCountries={onlyCountries}
          preferredCountries={preferredCountries}
          excludeCountries={excludeCountries}
          enableSearch={enableSearch}
          autoFormat={autoFormat}
          inputStyle={inputStyle}
          buttonStyle={buttonStyle}
          containerStyle={phoneInputStyle}
          inputClass={phoneInputClass}
          isValid={(inputNumber, country: any, countries) => {
            // Validate that the number has exactly 10-11 digits (excluding country code)
            const nationalNumber = inputNumber.replace(
              country?.dialCode || '',
              '',
            );
            const cleanNumber = nationalNumber.replace(/\D/g, '');
            return cleanNumber.length >= 10 && cleanNumber.length <= 11;
          }}
          inputProps={{
            id: id,
            name: name,
            required: isRequired && validationBehavior === 'native',
            readOnly: isReadOnly,
            maxLength: 15, // Country code + 11 digits + formatting
            'aria-invalid': isInvalid,
            'aria-required': isRequired && validationBehavior === 'aria',
            'aria-readonly': isReadOnly,
            'aria-disabled': isDisabled || disabled,
            'aria-describedby': description ? `${id}-description` : undefined,
            'data-invalid': isInvalid,
            'data-required': isRequired,
            'data-readonly': isReadOnly,
            'data-disabled': isDisabled || disabled,
            onFocus: onFocus,
            onBlur: onBlur,
          }}
        />
        {endContent && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10">
            {endContent}
          </div>
        )}
        {label && labelPlacement === 'inside' && (
          <label
            htmlFor={id}
            className={`absolute left-12 top-1/2 transform -translate-y-1/2 text-foreground-500 pointer-events-none transition-all duration-200 ${labelClass}`}
          >
            {label}
          </label>
        )}
      </div>

      {description && (
        <div
          id={`${id}-description`}
          className="text-tiny text-foreground-400 pt-1"
        >
          {description}
        </div>
      )}

      {errorMessage && (
        <div className="text-tiny text-danger pt-1">{errorMessage}</div>
      )}

      {children}
    </div>
  );
};

SignupPhoneNumberInput.displayName = 'SignupPhoneNumberInput';

export default SignupPhoneNumberInput;
